import Breadcrumb from "@/components/Breadcrumb";
import RangeDatePicker from "@/components/DateRangePicker/DateRangePicker";
import GlobalSearch from "@/components/GlobalSearch";
import ImportPreviewBanner from "@/components/ImportPreviewBanner";
import OpenPageInVSCode from "@/components/OpenPageInVSCode";
import PortalComponentToggle from "@/components/PortalComponentToggle";
import ThemeToggle from "@/components/ThemeToggle";
import Row from "@/components/Ui/Row";
import UserButton from "@/components/UserButton/UserButton";
import DataInspector from "@/components/portal/DataInspector";
import { Button } from "@/components/shadcn/button";
import { useSidebar } from "@/components/shadcn/sidebar";
import { IS_DEVELOPMENT, IS_PRODUCTION } from "@/constants/env";
import { useSelectedDatesContext } from "@/contexts/selected-dates-context";
import { useVendorContext } from "@/contexts/vendor-context";
import { Menu } from "lucide-react";
import { ReactNode } from "react";
import { Range as RangeDate } from "react-date-range";
import { twMerge } from "tailwind-merge";

type PageHeaderProps = {
  showDatePicker?: boolean;
  headline?: string | JSX.Element | null;
  actions?: JSX.Element;
  className?: string;
  children?: ReactNode;
  hideGlobalSearch?: boolean;
  data?: unknown;
};

const PageHeader = ({
  showDatePicker,
  actions,
  className = "",
  children,
  hideGlobalSearch,
  data,
}: PageHeaderProps) => {
  const { selectedDates, handleDatesUpdate } = useSelectedDatesContext();
  const { selectedVendor } = useVendorContext();
  const { toggleSidebar } = useSidebar();

  const handleDateChange = async (value: RangeDate) => {
    const { startDate, endDate } = value;
    handleDatesUpdate({ startDate, endDate });
  };

  return (
    <>
      <div
        data-testid="page-header"
        className={twMerge(
          "bg-background sticky top-0 z-20 w-full py-2",
          className,
        )}
      >
        <Row
          className="min-h-9 w-full flex-nowrap! justify-between! gap-2"
          up
          left
        >
          <Row className="flex-nowrap gap-3" left>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 shrink-0 lg:hidden"
              onClick={toggleSidebar}
              aria-label="Toggle sidebar"
            >
              <Menu />
            </Button>
            <Breadcrumb />
          </Row>

          <Row className="gap-2">
            {!hideGlobalSearch && <GlobalSearch />}

            {selectedVendor && showDatePicker && (
              <div className="relative z-10 -my-px">
                <RangeDatePicker
                  onChange={handleDateChange}
                  initDates={selectedDates}
                />
              </div>
            )}

            {actions && <div className="row-right gap-2">{actions}</div>}

            {!IS_PRODUCTION && <DataInspector data={data} />}
            {IS_DEVELOPMENT && <PortalComponentToggle />}
            {IS_DEVELOPMENT && <OpenPageInVSCode />}
            {!IS_PRODUCTION && <ThemeToggle />}
            <UserButton />
          </Row>
        </Row>
        <ImportPreviewBanner />
      </div>
      <div className="w-full">
        <div className="mx-auto max-w-[1980px]">{children}</div>
      </div>
    </>
  );
};

export default PageHeader;
